import { Button, Grid, IconButton, makeStyles, Paper, Tooltip, Typography, useMediaQuery } from '@material-ui/core';
import { Apps, Build, DoubleArrow, Email, Share } from '@material-ui/icons';
import LinkIcon from '@material-ui/icons/Link';
import clsx from 'clsx';
import { isEmpty } from 'lodash';
import { memo, useState } from 'react';
import { TFuncKey, useTranslation } from 'react-i18next';
import CarDetailBulletList from 'src/containers/CarDetailBulletList';
import { useCustomerMode } from 'src/hooks/useCustomerMode';
import useCustomSnackbarGlobal from 'src/hooks/useSnackbarGlobal';
import { FUELS_WITH_BATTERY_CAPACITY } from 'src/modules/global-vars';

import BidBuy from '../containers/BidBuy';
import CarDetailUserInformation from '../containers/CarDetailUserInformation';
import PackageDetail from '../containers/PackageDetail';
import { useCurrency } from '../hooks/useCurrency';
import { useFeatureFlags } from '../hooks/useFeatureFlags';
import { UserPermissions, useUserPermissions } from '../hooks/useUserPermissions';
import { SOURCES_BID_ALLOWED } from '../modules/bid-buy-helpers';
import {
  BidSuccessStatus,
  DetailCarDTO,
  IndividualBidStateInfoDTO,
  Recommended,
  SourceType,
  ValidityType,
} from '../modules/generated/api';
import { engineFuelDetails, engineFuelOld, getLabel } from '../modules/labels';

import ActionDetailLink from './ActionDetailLink';
import BidInfoTable from './BidInfoTable';
import CarDetailAdditionalCarInfo from './CarDetailAdditionalCarInfo';
import CarDetailBoughtCarDeliveryAddress from './CarDetailBoughtCarDeliveryAddress';
import CarDetailCarBasics from './CarDetailCarBasics';
import CarDetailDamageTable, { areDamagesGrouped } from './CarDetailDamageTable';
import CarDetailEquipmentContainer from './CarDetailEquipmentContainer';
import CarDetailRecommendation from './CarDetailRecommendation';
import CarDetailTileBattery from './CarDetailTileBattery';
import CarDetailTileCo2 from './CarDetailTileCo2';
import CarDetailTileEngine from './CarDetailTileEngine';
import CarDetailTileFirstReg from './CarDetailTileFirstReg';
import CarDetailTileHighlights from './CarDetailTileHighlights';
import CarDetailTileTaxes from './CarDetailTileTaxes';
import CarDetailTileUnit from './CarDetailTileUnit';
import CarDetailTransmission from './CarDetailTransmission';
import CarDetailWarrantyInfoPrint from './CarDetailWarrantyInfoPrint';
import CarDetailWheel from './CarDetailWheel';
import { useBidsRequestAutomationStatusByCar } from './cars-table-next/useBids';
import CarsTableImageCell from './CarsTableImageCell';
import DetailDialog from './DetailDialog';
import PrevAuctionsDetailTable from './PrevAuctionsDetailTable';
import RefCarDialog from './RefCarDialog';
import { Menu, MenuAction, useMenu } from './ui';

enum ShareAction {
  Mail = 'mail',
  Clipboard = 'clipboard',
}

// Seller notes arrive as German free text; only wording we know verbatim can be translated.
const KNOWN_ADDITIONAL_INFORMATION: Record<string, TFuncKey<'translation', 'car.additionalInformationTexts'>> = {
  'Dieses Fahrzeug wird ohne vorliegende Zulassungsbescheinigung Teil 1 verkauft und ist diesbezüglich von der Reklamation ausgeschlossen.':
    'withoutRegistrationCertificate',
};

const useStyles = makeStyles((theme) => ({
  card: {
    marginTop: theme.spacing(2),
  },
  flexWidth: {
    '@media screen and (min-width: 1600px) and (max-width: 1800px)': {
      maxWidth: '80%',
      flexBasis: '80%',
    },
    '@media screen and (min-width: 1801px)': {
      paddingRight: theme.spacing(4),
    },
  },
  fullWidth: {
    '@media screen and (min-width: 960px) and (max-width: 1800px)': {
      flexBasis: '100%',
      maxWidth: '100%',
    },
  },
  heading: {
    marginBottom: theme.spacing(2),
  },
  imageWrapper: {
    background: theme.palette.grey['200'],
    borderRadius: 0,
    '@media print': {
      boxShadow: 'none',
    },
  },
  highlights: {
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
  },
  bulletListInformation: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  additionalInfoCard: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  gridWidth: {
    justifyContent: 'flex-start',
  },
  printHide: {
    '@media print': {
      display: 'none',
    },
  },
  share: {
    verticalAlign: 'bottom',
    display: 'inline',
    '@media print': {
      display: 'none',
    },
  },
  screenOnly: {
    '@media print': {
      display: 'none !important',
    },
  },
  printOnly: {
    '@media screen': {
      display: 'none',
    },
    '@media print': {
      marginTop: theme.spacing(2),
      marginBottom: theme.spacing(-2),
    },
  },
  printPage: {
    '@media print': {
      height: '98vh',
      overflow: 'visible',
      pageBreakAfter: 'always',
      display: 'flex !important',
    },
  },
  printColImage: {
    '@media print': {
      width: '40%',
      maxWidth: '40%',
      flex: '1 0 auto',
    },
  },
  printColKeyFacts: {
    '@media print': {
      width: '60%',
      maxWidth: '60%',
      flex: '1 0 auto',
    },
  },
  printHalfWidth: {
    '@media print': {
      width: '50%',
      maxWidth: '50%',
      flex: '1 0 auto',
    },
  },
  printBlock: {
    '@media print': {
      display: 'block !important',
    },
  },
  printUserDetail: {
    '@media print': {
      paddingTop: theme.spacing(2),
      '& .editBtn': {
        display: 'none',
      },
      '& .MuiAlert-icon': {
        display: 'none',
      },
      '& form': {
        width: '95vw',
      },
    },
  },
  packageHintPaper: {
    padding: theme.spacing(2),
    marginTop: theme.spacing(2),
  },
  packageHintText: {
    marginBottom: theme.spacing(1),
  },
  packageIdRow: {
    display: 'inline-flex',
    gap: '5px',
    alignItems: 'center',
    marginBottom: theme.spacing(1),
    '& svg': {
      filter: 'invert(46%) sepia(0%) saturate(15%) brightness(98%) contrast(97%)',
    },
  },
}));

type CarDetailContentProps = {
  car: DetailCarDTO;
  refresh: () => Promise<void>;
};

const getMailTo = (subject: string, body: string): string => {
  const template = `mailto:?subject=${subject}&body=${body}`;
  return encodeURI(template);
};

const getCarShareLink = (car: DetailCarDTO): string => {
  const pathname = car.source && car.carId ? `/cars/${car.source}/${car.carId}` : '';
  return [window.location.origin, pathname].join('');
};

const CarDetailContent = ({ car, refresh }: CarDetailContentProps) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const isBigScreen = useMediaQuery('(min-width: 1801px)');
  const packageViewFlagEnabled = useFeatureFlags('packageView');
  const hasPackageViewPermission = useUserPermissions(UserPermissions.PACKAGE_VIEW);
  const showPackageView = packageViewFlagEnabled && hasPackageViewPermission;
  const recommendationFlag = useFeatureFlags('irisRecommendation');
  const recommendationPermission = useUserPermissions(UserPermissions.IRIS_RECOMMENDATION);
  const isRecommendationEnabled = recommendationFlag && recommendationPermission;
  const { customerModeActive } = useCustomerMode();
  const [packageDialogOpen, setPackageDialogOpen] = useState(false);
  const otherBought = car.bidStateInfo?.otherCurrentBids
    ?.concat(car.bidStateInfo?.allHistoricBids || [])
    ?.find((otherBid) => otherBid.status === BidSuccessStatus.Bought);
  const isCarBought = car.bidSuccessStatus === BidSuccessStatus.Bought || !!otherBought;
  const hasGroupedDamages = areDamagesGrouped(car.damage, car.source);
  const carDetailLink = getCarShareLink(car);
  const { format } = useCurrency();
  const { data: bidAutomationStatus, refetch: refetchBidAutomationStatus } = useBidsRequestAutomationStatusByCar({
    sourceType: car.source!,
    carId: car.carId || '',
  });

  const { isOpen: isShareMenuOpen, open: openShareMenu, close: closeShareMenu } = useMenu();
  const { showError, showSuccess } = useCustomSnackbarGlobal();

  const getFuelLabel = () => {
    if (car.normalized?.fuelDetail) {
      return getLabel(engineFuelDetails, car.normalized.fuelDetail);
    }
    if (car.normalized?.fuel) {
      return getLabel(engineFuelOld, car.normalized.fuel);
    }
    return t('car.power');
  };

  const shareMenuReducer = (action: MenuAction) => {
    switch (action.id) {
      case ShareAction.Mail:
        window.open(
          getMailTo(
            t('carDetailContent.share.subject', { car: car.model }),
            t('carDetailContent.share.body', {
              car: car.model,
              price: car.potential?.base?.amount ? format(car.potential.base, {}) : 'n.A.',
              shareLink: carDetailLink,
            }),
          ),
          'blank',
        );
        break;
      case ShareAction.Clipboard:
        if ('writeText' in navigator.clipboard) {
          navigator.clipboard
            .writeText(getCarShareLink(car))
            .then(() => showSuccess(t('carDetailContent.share.actions.clipboard.success')))
            .catch(() => showError(t('carDetailContent.share.actions.clipboard.error')));
        } else {
          showError(t('carDetailContent.share.actions.clipboard.unsupported'));
        }
        break;
      // no default
    }
  };

  const shareActions: MenuAction[] = [
    {
      id: ShareAction.Mail,
      title: t('carDetailContent.share.actions.mail.label'),
      icon: Email,
    },
    {
      id: ShareAction.Clipboard,
      title: t('carDetailContent.share.actions.clipboard.label'),
      icon: LinkIcon,
    },
  ];

  const showBidBuy =
    !customerModeActive &&
    car.validity === ValidityType.Current &&
    car.source &&
    SOURCES_BID_ALLOWED.includes(car.source) &&
    car.marketingChannel?.length;

  if (window.location.hash === '#delivery') {
    setTimeout(() => {
      const element = document.getElementById('delivery');
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      window.history.replaceState(null, '', window.location.pathname + window.location.search);
    }, 1000);
  }

  return (
    <>
      <Typography variant="h1" className={classes.heading}>
        {car.model}{' '}
        <Menu
          open={isShareMenuOpen}
          onOpen={openShareMenu}
          onClose={closeShareMenu}
          actions={shareActions}
          onAction={shareMenuReducer}
          className={classes.share}
        >
          {(triggerProps) => (
            <IconButton {...triggerProps} color="primary">
              <Share />
            </IconButton>
          )}
        </Menu>
      </Typography>

      <Grid container spacing={4}>
        <Grid item xs={12} className={classes.printPage}>
          <Grid container spacing={4}>
            <Grid item xs={12} sm={12} md={6} lg={4} className={classes.printColImage}>
              <Paper elevation={3} className={classes.imageWrapper}>
                <CarsTableImageCell
                  bought={car.bidSuccessStatus === BidSuccessStatus.Bought}
                  otherBought={otherBought}
                  isExpired={car.validity === ValidityType.Expired}
                  imageLinks={car.imageLinks}
                  thumbnail={
                    Array.isArray(car.virtualSources) &&
                    [SourceType.RealFirstCallOther, SourceType.RealFirstCallOwn].some((source) =>
                      car.virtualSources?.includes(source),
                    )
                      ? car.imageThumbnailLink
                      : undefined
                  }
                  size="big"
                  bidStatus={car.bidStateInfo}
                  vin={car.vin}
                  watchlist={car.watchlistIds}
                  showWatchlistIcon
                />
              </Paper>

              {!customerModeActive && isRecommendationEnabled && car.recommended === Recommended.Recommended && (
                <CarDetailRecommendation car={car} />
              )}

              <div className={classes.printUserDetail}>
                <CarDetailUserInformation
                  car={car}
                  carId={car.carId as string}
                  source={car.source as SourceType}
                  userComment={car.userComment}
                  userRatingNamePrefix="printCarDetail"
                  refresh={refresh}
                />
              </div>

              {showPackageView && car.packageId && (
                <Paper elevation={3} className={clsx(classes.packageHintPaper, classes.printHide)}>
                  <div className={classes.packageIdRow}>
                    <Tooltip title={`${t('common.package')}-ID`}>
                      <Apps />
                    </Tooltip>
                    <Typography variant="body2" component="span">
                      {t('common.package')} {car.packageId}
                      {car.nrOfCarsInPackage !== undefined &&
                        ` - ${t('common.carsWithCount', { count: car.nrOfCarsInPackage })}`}
                    </Typography>
                  </div>
                  {showBidBuy && (
                    <Typography variant="body2" className={classes.packageHintText}>
                      {t('bids.goToPackageHint')}
                    </Typography>
                  )}
                  <Button variant="contained" color="primary" fullWidth onClick={() => setPackageDialogOpen(true)}>
                    {t('bids.goToPackage')}
                  </Button>
                </Paper>
              )}

              {packageDialogOpen && car.packageId && car.source && (
                <DetailDialog
                  open
                  maxItems={0}
                  onClose={() => setPackageDialogOpen(false)}
                  actionsTopRight={
                    <ActionDetailLink
                      key="detail-link"
                      href={`/cars/${car.source}/package/${car.packageId}`}
                      tooltipPosition="left"
                    />
                  }
                >
                  <PackageDetail source={car.source as SourceType} packageId={car.packageId} />
                </DetailDialog>
              )}

              {!isEmpty(car.additionalInformation) && (
                <div className={classes.bulletListInformation}>
                  <CarDetailBulletList
                    title={t('car.bulletListInformation')}
                    infoElements={car.additionalInformation!.map((info) => {
                      const key = KNOWN_ADDITIONAL_INFORMATION[info.trim()];
                      return key ? t(`car.additionalInformationTexts.${key}`) : info;
                    })}
                  />
                </div>
              )}

              {!customerModeActive && (
                <div className={classes.additionalInfoCard}>
                  <CarDetailAdditionalCarInfo car={car} />
                </div>
              )}
            </Grid>

            <Grid item xs={12} sm={12} md={6} lg={8} className={classes.printColKeyFacts}>
              <Grid container className={classes.gridWidth}>
                <Grid item xs={6} lg={3} md={6}>
                  <CarDetailTileUnit value={car.mileage} unit="km" label={t('car.mileage')} icon={DoubleArrow} />
                </Grid>
                <Grid item xs={6} lg={3} md={6}>
                  <CarDetailTileFirstReg
                    firstRegistration={car.firstRegistration}
                    constructionYear={car.constructionYear}
                  />
                </Grid>

                <Grid item xs={6} lg={3} md={6}>
                  <CarDetailTransmission transmissionType={car.normalized?.transmission} />
                </Grid>

                <Grid item xs={6} lg={3} md={6}>
                  <CarDetailTileEngine kW={car.enginePower} hp={car.enginePowerHp} fuelType={getFuelLabel()} />
                </Grid>

                <Grid item xs={6} lg={3} md={6}>
                  <CarDetailTileCo2
                    approxCo2={car.co2ApproximationMethod}
                    engineCo2={car.engineCo2}
                    engineCo2Nedc={car.engineCo2Nedc}
                    engineCo2Wltp={car.engineCo2Wltp}
                  />
                </Grid>
                <Grid item xs={6} lg={3} md={6}>
                  <CarDetailTileUnit
                    unit={car.priceDamage?.unit || '€'}
                    value={car.priceDamage?.value || 0}
                    label={t('car.damage')}
                    icon={Build}
                  />
                </Grid>

                {!customerModeActive && car.tax && (
                  <Grid item xs={6} lg={3} md={6}>
                    <CarDetailTileTaxes tax={car.tax} valuationCountry={car.potential?.country} />
                  </Grid>
                )}

                {car.normalized?.fuelDetail &&
                  FUELS_WITH_BATTERY_CAPACITY.includes(car.normalized.fuelDetail) &&
                  (car.normalized?.augmented?.batteryCapacityGross ||
                    car.battery?.stateOfHealth?.condition !== undefined) && (
                    <Grid item xs={6} lg={3} md={6}>
                      <CarDetailTileBattery
                        batteryCapacityGross={car.normalized.augmented?.batteryCapacityGross}
                        stateOfHealth={car.battery?.stateOfHealth}
                      />
                    </Grid>
                  )}

                <Grid item xs={12} className={classes.highlights}>
                  <CarDetailTileHighlights car={car} damageType={car.damageType} />
                </Grid>

                <Grid item xs={12}>
                  <Grid container>
                    <Grid className={classes.flexWidth} item lg xs={12}>
                      <CarDetailCarBasics
                        disableHeader
                        car={car}
                        isBought={isCarBought}
                        otherBought={otherBought}
                        refresh={refresh}
                      />
                    </Grid>
                    {showBidBuy && isBigScreen && (!car.packageId || !showPackageView) && (
                      <Grid lg item className={classes.screenOnly}>
                        <BidBuy car={car} refresh={refresh} />
                      </Grid>
                    )}
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>

        <Grid item xs={12} container spacing={4} alignContent="flex-start" className={classes.printPage}>
          {showBidBuy && !isBigScreen && (!car.packageId || !showPackageView) && (
            <Grid item md={6} xs={12} className={classes.screenOnly}>
              <BidBuy car={car} refresh={refresh} />
            </Grid>
          )}

          <Grid
            item
            xs={12}
            xl={6}
            md={customerModeActive || hasGroupedDamages ? 6 : 4}
            className={isCarBought ? classes.printHalfWidth : undefined}
          >
            <CarDetailDamageTable damages={car.damage} source={car.source} />
            {!isEmpty(car.warrantyInformation) && (
              <div className={classes.printOnly}>
                <CarDetailWarrantyInfoPrint warrantyInfo={car.warrantyInformation} disableElevation={false} />
              </div>
            )}
          </Grid>

          {customerModeActive && (
            <Grid item xs={12} md={6} xl={6}>
              <CarDetailAdditionalCarInfo car={car} />
            </Grid>
          )}

          {!customerModeActive && (
            <Grid
              className={clsx(
                classes.screenOnly,
                car.validity === ValidityType.Current && (car.previousAuctions?.length || 0) > 2 && classes.fullWidth,
              )}
              container
              item
              xl={6}
              md={hasGroupedDamages ? 6 : 8}
              xs={12}
              direction="row"
              spacing={2}
            >
              {(!isEmpty(car.bidStateInfo?.allHistoricBids) ||
                !isEmpty(car.bidStateInfo?.otherCurrentBids) ||
                !isEmpty(car.bidStateInfo?.ownCurrentBids)) && (
                <Grid item xs={12}>
                  <BidInfoTable
                    bids={car.bidStateInfo || ({} as IndividualBidStateInfoDTO)}
                    title={t('bids.bidHammers.bidsOverview')}
                    source={car.source}
                    refresh={refresh}
                    dateEnd={car.dateEnd}
                    bidId={car.bid?.id}
                    carId={car.carId}
                    bidAutomationStatus={bidAutomationStatus}
                    bidAutomationRefresh={refetchBidAutomationStatus}
                  />
                </Grid>
              )}
              <Grid item xs={12}>
                <PrevAuctionsDetailTable
                  title={`${t('bids.prevAuctions')} (${car.previousAuctions?.length})`}
                  car={car}
                />
              </Grid>
            </Grid>
          )}

          {!customerModeActive && isCarBought && (
            <Grid item xs={6} id="delivery">
              <CarDetailBoughtCarDeliveryAddress car={car} />
            </Grid>
          )}

          {!isEmpty(car.wheels) && (
            <Grid item lg={car.wheels!.length > 4 ? 12 : 6} xs={12} className={classes.printOnly}>
              <CarDetailWheel wheels={car.wheels} />
            </Grid>
          )}
        </Grid>
      </Grid>

      <Grid container spacing={4} className={classes.printBlock}>
        <CarDetailEquipmentContainer equipments={car.equipmentCodeText ?? []} />
        {!isEmpty(car.wheels) && (
          <Grid item lg={car.wheels!.length > 4 ? 12 : 6} xs={12} className={classes.screenOnly}>
            <CarDetailWheel wheels={car.wheels} />
          </Grid>
        )}
      </Grid>
      <RefCarDialog />
    </>
  );
};
export default memo(CarDetailContent);
